/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import type {
  FunctionComponent,
  HTMLProps,
  KeyboardEvent,
  RefObject,
} from 'react'
import React from 'react'

import classnames from 'classnames'
import FocusTrap from 'focus-trap-react'

import { CrossIcon } from '../Icons/Navigational/Cross'
import { TypographyV2 as Typography } from '../Seatbelt2/Foundations/Typography'
import styles from './TooltipWithButton/TooltipWithButton.module.scss'

export const Tooltip: FunctionComponent<TooltipProps> = ({
  contents,
  title,
  tooltipClassName,
  isTooltipOpen,
  isTooltipDisplayedAbove,
  tooltipAlignment,
  tooltipRef,
  tooltipCloseButtonRef,
  handleTooltipClick,
  handleTooltipKeyDown,
  handleClickOutside,
  closeHandler,
  ...rest
}) => {
  const handleTooltipClickEvent = (e: React.MouseEvent) => {
    if (closeHandler && isTooltipOpen) {
      closeHandler()
    }

    handleTooltipClick(e)
  }

  const handleTooltipKeyDownEvent = (e: React.KeyboardEvent) => {
    if (closeHandler && isTooltipOpen && e.key !== 'Tab') {
      closeHandler()
    }

    handleTooltipKeyDown(e)
  }

  return (
    <>
      {isTooltipOpen && (
        <FocusTrap
          focusTrapOptions={{
            initialFocus: '#tooltipText',
            clickOutsideDeactivates: (e) => {
              handleClickOutside(e)
              return true
            },
            tabbableOptions: {
              displayCheck: 'none',
            },
          }}
        >
          <div
            data-testid="tool-tip"
            data-which-id="tool-tip"
            className={classnames(
              styles.tooltip,
              isTooltipDisplayedAbove && styles.displayedAbove,
              tooltipAlignment === 'left' && styles.alignedLeft,
              tooltipAlignment === 'right' && styles.alignedRight,
              tooltipClassName
            )}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleTooltipKeyDownEvent}
            ref={tooltipRef}
            role="dialog"
            id="tooltip"
            aria-modal="true"
            aria-label="tool-tip"
            {...rest}
          >
            <button
              data-testid="close-button"
              className={styles.close}
              aria-label="close tooltip"
              onClick={handleTooltipClickEvent}
              onKeyDown={handleTooltipKeyDownEvent}
              type="button"
            >
              <CrossIcon
                iconRef={tooltipCloseButtonRef}
                width={15}
                height={15}
              />
            </button>
            <div id="tooltipText" tabIndex={-1}>
              {title && (
                <Typography
                  tag="h3"
                  textStyle="sb-text-body-small-regular"
                  className={styles.heading}
                >
                  {title}
                </Typography>
              )}
              <Typography
                textStyle="sb-text-body-x-small-regular"
                className={styles.contents}
                data-which-id="tooltip-body"
                data-testid="tooltip-body"
                dangerouslySetInnerHTML={{ __html: contents }}
              />
            </div>
          </div>
        </FocusTrap>
      )}
    </>
  )
}

///////// IMPLEMENTATION /////////

export type TooltipProps = {
  contents: string
  title?: string
  tooltipClassName?: string
  isTooltipOpen: boolean
  isTooltipDisplayedAbove: boolean
  tooltipAlignment?: string
  tooltipRef: RefObject<HTMLDivElement>
  tooltipCloseButtonRef: RefObject<SVGSVGElement>
  handleTooltipClick: (e: React.MouseEvent) => unknown
  handleTooltipKeyDown: (e: KeyboardEvent) => unknown
  className?: string
  handleClickOutside: (e: MouseEvent | TouchEvent) => unknown
  closeHandler?: () => void
} & HTMLProps<HTMLDivElement>
